import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import CaptureSession from "../utils/CaptureSession";
import { useTheme } from "@mui/material";
import {
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Divider,
  Chip,
  Box,
  Button,
  FormHelperText,
  Typography,
  Container,
  Card,
  CardContent,
  Breadcrumbs,
  Link,
  Autocomplete,
  Modal,
  Alert,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import "dayjs/locale/es";
import Restricted from "components/Restricted";

import HomeIcon from "@mui/icons-material/Home";
import DepartureBoardOutlined from "@mui/icons-material/DepartureBoardOutlined";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import AssignmentIcon from "@mui/icons-material/Assignment";
import Face3Icon from "@mui/icons-material/Face3";
import { calculateAgeYears } from "../utils/preview";
import AnticonCaptureControls from "./AnticonCaptureControls";
import AnticonDoctorSelect from "./AnticonDoctorSelect";
import useAnticonContext from "./useAnticonContext";
import { resolveContextFields } from "../utils/convocatoriaContext";
import Select from "../utils/RecordedSelect";
import { canWriteCapture, useSaludV2Capture } from "../utils/useSaludV2Capture";
import { antiConFormSnapshot, hydrateV2DataToForm, serializeFormToV2Data } from "../utils/antiConAdapter";

// Función para capitalizar la primera letra de cada palabra
const capitalizeWords = (str) => {
  if (!str) return "";
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

// Mapeos y rangos para la lógica de códigos
const grupoEtnicoMap = {
  1: "Mestizo",
  2: "Wayuu",
  3: "Añú",
  4: "Japreira",
  5: "Yanomami",
  6: "Bari",
};
const grupoEtnicoRange = { min: 1, max: 6 };

const discapacidadMap = {
  1: "Física",
  2: "Intelectual",
  3: "Sensorial",
  4: "Múltiple",
  5: "Ninguna",
};
const discapacidadRange = { min: 1, max: 5 };

const condicionMovilidadMap = {
  1: "Local",
  2: "Desplazado Internos",
  3: "Refugiado",
  4: "Retornado",
  5: "Pendular",
};
const condicionMovilidadRange = { min: 1, max: 5 };

const factoresRiesgoMap = {
  1: "Abuso Psicológico",
  2: "Abuso Físico",
  3: "Abuso Sexual",
  4: "Embarazo Adolescente",
  5: "Explotación Sexual",
  6: "Falta de Documento de Identidad",
  7: "Matrimonio Infantil",
  8: "Negligencia",
  9: "No Acompañado (< 18 años)",
  10: "Trabajo Infantil",
  11: "Trastorno Mental",
  12: "No Aplica",
};
const factoresRiesgoRange = { min: 1, max: 12 };

const nivelEducativoMap = {
  1: "Prescolar",
  2: "Primaria",
  3: "Secundaria",
  4: "Universitario",
  5: "Sin Estudios",
  6: "Analfabeta",
  7: "N/A",
};
const nivelEducativoRange = { min: 1, max: 7 };

const metodoAnticonceptivoMap = {
  1: "Orales",
  2: "Implantes de 3 años",
  3: "Implantes de 5 años",
  4: "Inyecciones Trimestrales",
  5: "Inyecciones Mensuales",
  6: "DIU",
  7: "Preservativo",
};
const metodoAnticonceptivoRange = { min: 1, max: 7 };

const diagnosticoItsMap = {
  1: "Vaginosis Bacteriana",
  2: "Vaginitis Micótica",
  3: "Vaginitis y Cervicitis",
  4: "Sífilis Primaria",
  5: "Sífilis Secundaria",
  6: "Úlcera Vaginal",
  7: "Chancro Blando",
  8: "Herpes Genital",
  9: "Linfogranuloma Venéreo",
  10: "Granuloma Inguinal",
  11: "Secreción Uretral",
  12: "VIH",
  13: "VPH",
  14: "Hepatitis B",
  15: "N/A",
};
const diagnosticoItsRange = { min: 1, max: 15 };


function AntiConFormV2() {
  return <CaptureSession component={AnticonCapture} />;
}

function AnticonCapture({ onCreated, onNavigationState }) {
  const [items, setItems] = React.useState([]);

  // Estados para la nueva sección de Info General
  const [estadoSalud, setEstadoSalud] = useState("");
  const [municipioSalud, setMunicipioSalud] = useState("");
  const [parroquiaSalud, setParroquiaSalud] = useState("");
  const [estSalud, setEstSalud] = useState("");
  const [fechaActSalud, setFechaActSalud] = useState(null);

  useEffect(() => {
    let items;
    try { items = JSON.parse(localStorage.getItem("token")); } catch (_) { items = null; }
    if (items) {
      setItems(items);
    }
  }, []);

  const rol = String(items.rol || "");
  const nav = useNavigate();
  const theme = useTheme();

  const [formData, setFormData] = useState({
    beneficiaria: {
      codigo: "",
      primerNombre: "",
      primerApellido: "",
      genero: "",
      cedulaIdentidad: "",
      numeroTelefonico: "",
      fechaNacimiento: null,
    },
    representante: {
      primerNombre: "",
      primerApellido: "",
      cedulaIdentidad: "",
    },
    demograficosBeneficiaria: {
      lgbtqi: "",
      comunidad: "",
      codigoComunidad: "",
      grupoEtnico: "",
      codigoGrupoEtnico: "",
      discapacidad: "",
      codigoDiscapacidad: "",
      condicionMovilidad: "",
      codigoCondicionMovilidad: "",
      factoresRiesgo: "",
      codigoFactoresRiesgo: "",
      codigoNivelEducativo: "",
      nivelEducativo: "",
      ingresosPropios: "",
      cabezaFamilia: "",
    },
    atencion: {
      pruebaEmbarazo: "",
      codigoAtencion: "",
      metodoAnticonceptivo: "",
    },
    manejoITS: {
      pruebaVIH: "",
      pruebaSifilis: "",
      pruebaHepatitis: "",
      codigoDiagnosticoITS: "",
      diagnosticoITS: "",
      tratamientoITS: [],
    },
    adicionales: { comentarios: "", medicoResponsable: null },
  });
  const [errors, setErrors] = useState({});
  const [isRepresentanteDisabled, setIsRepresentanteDisabled] = useState(false);
  const [edadDeclarada, setEdadDeclarada] = useState("");
  const baselineRef = useRef({});
  const onHydrate = useCallback((datos) => {
    const loaded = hydrateV2DataToForm(datos);
    setFormData(loaded.formData);
    setEdadDeclarada(loaded.edadDeclarada);
    setFechaActSalud(loaded.fechaAtencion);
    setEstadoSalud(loaded.estado);
    setMunicipioSalud(loaded.municipio);
    setParroquiaSalud(loaded.parroquia);
    setEstSalud(loaded.centro);
    baselineRef.current = antiConFormSnapshot(loaded);
  }, []);
  const capture = useSaludV2Capture("ANTICONCEPTIVOS", onHydrate, { onCreated });
  useEffect(() => onNavigationState({ busy: capture.busy, uncertain: capture.uncertain, dirty: capture.dirty }),
    [onNavigationState, capture.busy, capture.uncertain, capture.dirty]);
  const canWrite = canWriteCapture(rol, items.id, capture.record);
  const journey = useAnticonContext(capture);
  const general = resolveContextFields({ estado: estadoSalud, municipio: municipioSalud,
    parroquia: parroquiaSalud, centro: estSalud, fecha: fechaActSalud },
    journey.defaults, {}, capture.record || capture.hasPendingSnapshot);
  const edadCalculada = general.fecha ? calculateAgeYears(formData.beneficiaria.fechaNacimiento, general.fecha) : null;
  const edadVisible = edadCalculada !== null ? edadCalculada : edadDeclarada;

  const {
    codigoGrupoEtnico,
    codigoDiscapacidad,
    codigoCondicionMovilidad,
    codigoFactoresRiesgo,
    codigoNivelEducativo,
  } = formData.demograficosBeneficiaria;
  const { codigoAtencion } = formData.atencion;
  const { codigoDiagnosticoITS } = formData.manejoITS;

  useEffect(() => setIsRepresentanteDisabled(edadVisible !== "" && edadVisible !== null && Number(edadVisible) >= 14), [edadVisible]);

  useEffect(() => {
    const value = grupoEtnicoMap[codigoGrupoEtnico] || "";
    setFormData((prev) => ({
      ...prev,
      demograficosBeneficiaria: {
        ...prev.demograficosBeneficiaria,
        grupoEtnico: value,
      },
    }));
  }, [codigoGrupoEtnico]);

  useEffect(() => {
    const value = discapacidadMap[codigoDiscapacidad] || "";
    setFormData((prev) => ({
      ...prev,
      demograficosBeneficiaria: {
        ...prev.demograficosBeneficiaria,
        discapacidad: value,
      },
    }));
  }, [codigoDiscapacidad]);

  useEffect(() => {
    const value = condicionMovilidadMap[codigoCondicionMovilidad] || "";
    setFormData((prev) => ({
      ...prev,
      demograficosBeneficiaria: {
        ...prev.demograficosBeneficiaria,
        condicionMovilidad: value,
      },
    }));
  }, [codigoCondicionMovilidad]);

  useEffect(() => {
    const value = factoresRiesgoMap[codigoFactoresRiesgo] || "";
    setFormData((prev) => ({
      ...prev,
      demograficosBeneficiaria: {
        ...prev.demograficosBeneficiaria,
        factoresRiesgo: value,
      },
    }));
  }, [codigoFactoresRiesgo]);

  useEffect(() => {
    const value = nivelEducativoMap[codigoNivelEducativo] || "";
    setFormData((prev) => ({
      ...prev,
      demograficosBeneficiaria: {
        ...prev.demograficosBeneficiaria,
        nivelEducativo: value,
      },
    }));
  }, [codigoNivelEducativo]);

  useEffect(() => {
    const value = metodoAnticonceptivoMap[codigoAtencion] || "";
    setFormData((prev) => ({
      ...prev,
      atencion: { ...prev.atencion, metodoAnticonceptivo: value },
    }));
  }, [codigoAtencion]);

  useEffect(() => {
    const value = diagnosticoItsMap[codigoDiagnosticoITS] || "";
    setFormData((prev) => ({
      ...prev,
      manejoITS: { ...prev.manejoITS, diagnosticoITS: value },
    }));
  }, [codigoDiagnosticoITS]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canWrite || !capture.qa.enabled || capture.busy) return;
    try {
      const snapshot = antiConFormSnapshot({ formData, edadDeclarada, fechaAtencion: general.fecha,
        estado: general.estado, municipio: general.municipio, parroquia: general.parroquia, centro: general.centro });
      const datos = serializeFormToV2Data(snapshot, capture.record?.datos, baselineRef.current);
      await capture.save(datos);
    } catch (error) {
      capture.setNotice({ severity: "error", text: error.message });
    }
  };

  const handleChange = (section, e) => {
    capture.setDirty(true);
    const { name, value } = e.target;

    setFormData((prev) => {
      const newSectionData = { ...prev[section] };
      let updatedValue = value;

      const fieldsToCapitalize = [
        "primerNombre",
        "primerApellido",
        "comunidad",
        "diagnosticoITS",
        "comentarios",
      ];
      const numericFields = [
        "codigoComunidad",
        "codigoGrupoEtnico",
        "codigoDiscapacidad",
        "codigoCondicionMovilidad",
        "codigoFactoresRiesgo",
        "codigoNivelEducativo",
        "codigoAtencion",
        "codigoDiagnosticoITS",
      ];

      if (
        numericFields.includes(name) &&
        value !== "" &&
        !/^\d+$/.test(value)
      ) {
        return prev;
      }

      if (fieldsToCapitalize.includes(name)) {
        updatedValue = capitalizeWords(value);
      } else if (name === "cedulaIdentidad") {
        updatedValue = value.slice(0, 10);
      }

      if (name === "codigoNivelEducativo" && value !== "" && !/^[1-7]$/.test(value)) return prev;

      newSectionData[name] = updatedValue;
      return { ...prev, [section]: newSectionData };
    });

    setErrors((prev) => {
      if (!prev[section]?.[name]) return prev;
      const newSectionErrors = { ...prev[section] };
      delete newSectionErrors[name];
      return { ...prev, [section]: newSectionErrors };
    });
  };

  const handleAutocompleteChange = (section, name, newValue) => {
    setFormData((prev) => ({
      ...prev,
      [section]: { ...prev[section], [name]: newValue },
    }));
    setErrors((prev) => {
      if (!prev[section]?.[name]) return prev;
      const newSectionErrors = { ...prev[section] };
      delete newSectionErrors[name];
      return { ...prev, [section]: newSectionErrors };
    });
  };

  const handleDateChange = (date) => {
    capture.setDirty(true);
    setFormData((p) => ({
      ...p,
      beneficiaria: { ...p.beneficiaria, fechaNacimiento: date },
    }));
    setErrors((prev) => {
      if (!prev.beneficiaria?.fechaNacimiento) return prev;
      const newBeneficiariaErrors = { ...prev.beneficiaria };
      delete newBeneficiariaErrors.fechaNacimiento;
      return { ...prev, beneficiaria: newBeneficiariaErrors };
    });
  };

  const formatPhoneNumber = (value) => {
    if (!value) return "";
    const d = value.replace(/\D/g, "");
    let f = "";
    if (d.length > 0) f += "+" + d.substring(0, 2);
    if (d.length > 2) f += "(" + d.substring(2, 5);
    if (d.length > 5) f += ")" + d.substring(5, 12);
    return f;
  };

  const handlePhoneChange = (e) => {
    capture.setDirty(true);
    const formattedValue = formatPhoneNumber(e.target.value);
    setFormData((p) => ({
      ...p,
      beneficiaria: { ...p.beneficiaria, numeroTelefonico: formattedValue },
    }));
  };

  function handleClickBreadCrumbs(event) {
    event.preventDefault();
  }

  //Modal Handlers
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  //Modal Styles
  const style = {
    position: "inherit",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "25%",
    bgcolor: theme.palette.background.alt,
    border: "2px solid #000",
    borderRadius: 4,
    boxShadow: 1,
    p: 4,
  };

  //Routes
  const handleLinkCancelar = (event, message) => {
    if (message === "cancelar") {
      nav(-1);
    }
  };

  const handleLinkHome = (event, message) => {
    if (message === "home") {
      nav("/inicio");
    }
  };

  const handleLinkTranscripcion = (event, message) => {
    if (message === "transcripcion") {
      nav("/transcripcion");
    }
  };

  const handleLinkConvos = (event, message) => {
    if (message === "convos") {
      nav(-1);
    }
  };

  useEffect(() => {
    if (!capture.dirty) return undefined;
    const beforeUnload = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [capture.dirty]);

  return (
    <Box m="1.5rem 2.5rem">
      {rol === "1" ||
      rol === "2" ||
      rol === "6" ||
      rol === "8" ||
      rol === "9" ||
      rol === "10" ||
      rol === "11" ? (
        <>
          <Box
            role="presentation"
            onClick={handleClickBreadCrumbs}
            sx={{ mb: 3 }}
          >
            <Breadcrumbs aria-label="breadcrumb">
              <Link
                underline="hover"
                sx={{ display: "flex", alignItems: "center" }}
                color="inherit"
                href="/inicio"
                onClick={(event) => handleLinkHome(event, "home")}
              >
                <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                Inicio
              </Link>
              <Link
                underline="hover"
                sx={{ display: "flex", alignItems: "center" }}
                color="inherit"
                href="/transcripcion"
                onClick={(event) =>
                  handleLinkTranscripcion(event, "transcripcion")
                }
              >
                <DriveFileRenameOutlineIcon
                  sx={{ mr: 0.5 }}
                  fontSize="inherit"
                />
                Transcripción
              </Link>
              <Link
                underline="hover"
                sx={{ display: "flex", alignItems: "center" }}
                color="inherit"
                href="/listaTranscripcion"
                onClick={(event) => handleLinkConvos(event, "convos")}
              >
                <AssignmentIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                Convocatoria
              </Link>
              <Typography
                sx={{ display: "flex", alignItems: "center" }}
                color="text.primary"
              >
                <Face3Icon sx={{ mr: 0.5 }} fontSize="inherit" />
                Nueva Beneficiaria
              </Typography>
            </Breadcrumbs>
            <Typography variant="h3" sx={{ fontWeight: "bold", mt: 2 }}>
              Nueva Beneficiaria (Métodos Anticonceptivos)
            </Typography>
            <AnticonCaptureControls capture={capture} role={rol} canWrite={canWrite} />
          </Box>
          {/*/////////////////  FORMULARIO  ////////////////////*/}
          <Container>
            <Card
              sx={{
                borderRadius: 8,
                boxShadow: 5,
                bgcolor: theme.palette.background.alt,
              }}
            >
              <CardContent>
                <Box
                  component="form"
                  onSubmit={handleSubmit}
                  onChangeCapture={() => capture.setDirty(true)}
                  noValidate
                  sx={{ flexGrow: 1, padding: 3 }}
                >
                  <fieldset disabled={!canWrite || !capture.qa.enabled || capture.busy || capture.uncertain} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
                  <Divider sx={{ mt: 4, mb: 3 }}>
                    <Chip label="INFO. GENERAL" />
                  </Divider>
                  <Grid container spacing={2}>
                    {journey.error && <Grid item xs={12}><Alert severity="warning">{journey.error}</Alert></Grid>}
                    {journey.context && !capture.record &&
                      [journey.defaults.estado, journey.defaults.municipio, journey.defaults.parroquia].some(value => !value) &&
                      <Grid item xs={12}><Alert severity="warning">Ubicación geográfica de la jornada incompleta o no disponible.</Alert></Grid>}
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Estado"
                        value={general.estado}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Municipio"
                        value={general.municipio}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Parroquia"
                        value={general.parroquia}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Comunidad"
                        inputProps={{ "aria-label": "Comunidad de la jornada" }}
                        helperText={capture.record ? "Valor original guardado (centro)" : ""}
                        value={general.centro}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="Fecha de Atención"
                        value={general.fecha && dayjs(general.fecha).isValid() ? dayjs(general.fecha).format("DD/MM/YYYY") : ""}
                        InputProps={{ readOnly: true }} />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }}>
                    <Chip
                      label="Datos de la Beneficiaria"
                      sx={{ textTransform: "uppercase" }}
                    />
                  </Divider>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Código Beneficiaria"
                        name="codigo"
                        value={formData.beneficiaria.codigo}
                        onChange={(e) => handleChange("beneficiaria", e)}
                        margin="dense"
                        error={!!errors.beneficiaria?.codigo}
                        helperText={errors.beneficiaria?.codigo || ""}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Primer Nombre"
                        name="primerNombre"
                        value={formData.beneficiaria.primerNombre}
                        onChange={(e) => handleChange("beneficiaria", e)}
                        margin="dense"
                        error={!!errors.beneficiaria?.primerNombre}
                        helperText={errors.beneficiaria?.primerNombre || ""}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Primer Apellido"
                        name="primerApellido"
                        value={formData.beneficiaria.primerApellido}
                        onChange={(e) => handleChange("beneficiaria", e)}
                        margin="dense"
                        error={!!errors.beneficiaria?.primerApellido}
                        helperText={errors.beneficiaria?.primerApellido || ""}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl
                        fullWidth
                        margin="dense"
                        error={!!errors.beneficiaria?.genero}
                      >
                        <InputLabel id="gen-l">Género</InputLabel>
                        <Select disabled={!canWrite || !capture.qa.enabled || capture.busy || capture.uncertain} onEdit={() => capture.setDirty(true)}
                          labelId="gen-l"
                          name="genero"
                          value={formData.beneficiaria.genero}
                          label="Género"
                          onChange={(e) => handleChange("beneficiaria", e)}
                        >
                          <MenuItem value="M">M</MenuItem>
                          <MenuItem value="F">F</MenuItem>
                          <MenuItem value="Otro">Otro</MenuItem>
                        </Select>
                        {errors.beneficiaria?.genero && (
                          <FormHelperText>
                            {errors.beneficiaria.genero}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Cédula de Identidad"
                        name="cedulaIdentidad"
                        type="number"
                        value={formData.beneficiaria.cedulaIdentidad}
                        onChange={(e) => handleChange("beneficiaria", e)}
                        inputProps={{ maxLength: 10 }}
                        margin="dense"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Número Telefónico"
                        name="numeroTelefonico"
                        value={formData.beneficiaria.numeroTelefonico}
                        onChange={handlePhoneChange}
                        placeholder="+XX(XXX)XXXXXXX"
                        margin="dense"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="Fecha de Nacimiento"
                          value={formData.beneficiaria.fechaNacimiento}
                          onChange={handleDateChange}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              margin="dense"
                              error={!!errors.beneficiaria?.fechaNacimiento}
                              helperText={
                                errors.beneficiaria?.fechaNacimiento || ""
                              }
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Edad"
                        type="number"
                        value={edadVisible}
                        onChange={(event) => {
                          const value = event.target.value;
                          if (value === "" || (/^\d{1,3}$/.test(value) && Number(value) <= 120)) {
                            setEdadDeclarada(value);
                          }
                        }}
                        disabled={edadCalculada !== null}
                        inputProps={{ min: 0, max: 120, step: 1 }}
                        margin="dense"
                        helperText={edadCalculada !== null ? "Calculada según fecha de atención" : "Edad declarada; deje vacío si se desconoce"}
                      />
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 4 }}>
                    <Chip
                      label="Datos del Representante"
                      sx={{ textTransform: "uppercase" }}
                    />
                  </Divider>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        disabled={isRepresentanteDisabled}
                        label="Primer Nombre"
                        name="primerNombre"
                        value={formData.representante.primerNombre}
                        onChange={(e) => handleChange("representante", e)}
                        margin="dense"
                        error={!!errors.representante?.primerNombre}
                        helperText={errors.representante?.primerNombre || ""}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        disabled={isRepresentanteDisabled}
                        label="Primer Apellido"
                        name="primerApellido"
                        value={formData.representante.primerApellido}
                        onChange={(e) => handleChange("representante", e)}
                        margin="dense"
                        error={!!errors.representante?.primerApellido}
                        helperText={errors.representante?.primerApellido || ""}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        disabled={isRepresentanteDisabled}
                        label="Cédula de Identidad"
                        name="cedulaIdentidad"
                        type={isRepresentanteDisabled ? "text" : "number"}
                        value={formData.representante.cedulaIdentidad}
                        onChange={(e) => handleChange("representante", e)}
                        inputProps={{ maxLength: 10 }}
                        margin="dense"
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 4 }}>
                    <Chip
                      label="Datos Demográficos de la Beneficiaria"
                      sx={{ textTransform: "uppercase" }}
                    />
                  </Divider>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={3}>
                      <FormControl
                        fullWidth
                        margin="dense"
                        error={!!errors.demograficosBeneficiaria?.lgbtqi}
                      >
                        <InputLabel id="lgbtqi-l">LGBTQI+</InputLabel>
                        <Select disabled={!canWrite || !capture.qa.enabled || capture.busy || capture.uncertain} onEdit={() => capture.setDirty(true)}
                          labelId="lgbtqi-l"
                          name="lgbtqi"
                          value={formData.demograficosBeneficiaria.lgbtqi}
                          label="LGBTQI+"
                          onChange={(e) =>
                            handleChange("demograficosBeneficiaria", e)
                          }
                        >
                          <MenuItem value="Si">Si</MenuItem>
                          <MenuItem value="No">No</MenuItem>
                        </Select>
                        {errors.demograficosBeneficiaria?.lgbtqi && (
                          <FormHelperText>
                            {errors.demograficosBeneficiaria.lgbtqi}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Comunidad"
                        name="comunidad"
                        inputProps={{ "aria-label": "Comunidad de residencia" }}
                        value={formData.demograficosBeneficiaria.comunidad}
                        onChange={(e) =>
                          handleChange("demograficosBeneficiaria", e)
                        }
                        margin="dense"
                        error={!!errors.demograficosBeneficiaria?.comunidad}
                        helperText={
                          errors.demograficosBeneficiaria?.comunidad || ""
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Código Grupo Étnico"
                        name="codigoGrupoEtnico"
                        value={
                          formData.demograficosBeneficiaria.codigoGrupoEtnico
                        }
                        onChange={(e) =>
                          handleChange("demograficosBeneficiaria", e)
                        }
                        margin="dense"
                        error={
                          !!errors.demograficosBeneficiaria?.codigoGrupoEtnico
                        }
                        helperText={
                          errors.demograficosBeneficiaria?.codigoGrupoEtnico ||
                          `(Grupo Étnico) ${grupoEtnicoRange.min}-${grupoEtnicoRange.max}`
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        disabled
                        label="Grupo Étnico"
                        name="grupoEtnico"
                        value={formData.demograficosBeneficiaria.grupoEtnico}
                        margin="dense"
                        error={!!errors.demograficosBeneficiaria?.grupoEtnico}
                        helperText={
                          errors.demograficosBeneficiaria?.grupoEtnico || ""
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Código Discapacidad"
                        name="codigoDiscapacidad"
                        value={
                          formData.demograficosBeneficiaria.codigoDiscapacidad
                        }
                        onChange={(e) =>
                          handleChange("demograficosBeneficiaria", e)
                        }
                        margin="dense"
                        error={
                          !!errors.demograficosBeneficiaria?.codigoDiscapacidad
                        }
                        helperText={
                          errors.demograficosBeneficiaria?.codigoDiscapacidad ||
                          `(Discapacidad) ${discapacidadRange.min}-${discapacidadRange.max}`
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        disabled
                        label="Discapacidad"
                        name="discapacidad"
                        value={formData.demograficosBeneficiaria.discapacidad}
                        margin="dense"
                        error={!!errors.demograficosBeneficiaria?.discapacidad}
                        helperText={
                          errors.demograficosBeneficiaria?.discapacidad || ""
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Código Condición de Movilidad"
                        name="codigoCondicionMovilidad"
                        value={
                          formData.demograficosBeneficiaria.codigoCondicionMovilidad
                        }
                        onChange={(e) =>
                          handleChange("demograficosBeneficiaria", e)
                        }
                        margin="dense"
                        error={
                          !!errors.demograficosBeneficiaria?.codigoCondicionMovilidad
                        }
                        helperText={
                          errors.demograficosBeneficiaria?.codigoCondicionMovilidad ||
                          `(Cond. Movilidad) ${condicionMovilidadRange.min}-${condicionMovilidadRange.max}`
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        disabled
                        label="Condición de Movilidad"
                        name="condicionMovilidad"
                        value={
                          formData.demograficosBeneficiaria.condicionMovilidad
                        }
                        margin="dense"
                        error={
                          !!errors.demograficosBeneficiaria?.condicionMovilidad
                        }
                        helperText={
                          errors.demograficosBeneficiaria?.condicionMovilidad ||
                          ""
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Código Factores de Riesgo"
                        name="codigoFactoresRiesgo"
                        value={
                          formData.demograficosBeneficiaria
                            .codigoFactoresRiesgo
                        }
                        onChange={(e) =>
                          handleChange("demograficosBeneficiaria", e)
                        }
                        margin="dense"
                        error={
                          !!errors.demograficosBeneficiaria
                            ?.codigoFactoresRiesgo
                        }
                        helperText={
                          errors.demograficosBeneficiaria
                            ?.codigoFactoresRiesgo ||
                          `(Factor Riesgo) ${factoresRiesgoRange.min}-${factoresRiesgoRange.max}`
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        disabled
                        label="Factores de Riesgo"
                        name="factoresRiesgo"
                        value={formData.demograficosBeneficiaria.factoresRiesgo}
                        margin="dense"
                        error={
                          !!errors.demograficosBeneficiaria?.factoresRiesgo
                        }
                        helperText={
                          errors.demograficosBeneficiaria?.factoresRiesgo || ""
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Código Nivel Educativo"
                        name="codigoNivelEducativo"
                        value={
                          formData.demograficosBeneficiaria.codigoNivelEducativo
                        }
                        onChange={(e) =>
                          handleChange("demograficosBeneficiaria", e)
                        }
                        margin="dense"
                        error={
                          !!errors.demograficosBeneficiaria
                            ?.codigoNivelEducativo
                        }
                        helperText={
                          errors.demograficosBeneficiaria
                            ?.codigoNivelEducativo ||
                          `(Nivel Educativo) ${nivelEducativoRange.min}-${nivelEducativoRange.max}; 7 = N/A`
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        disabled
                        label="Nivel Educativo"
                        name="nivelEducativo"
                        value={formData.demograficosBeneficiaria.nivelEducativo}
                        margin="dense"
                        error={
                          !!errors.demograficosBeneficiaria?.nivelEducativo
                        }
                        helperText={
                          errors.demograficosBeneficiaria?.nivelEducativo || ""
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControl
                        fullWidth
                        margin="dense"
                        error={
                          !!errors.demograficosBeneficiaria?.ingresosPropios
                        }
                      >
                        <InputLabel id="ingresos-l">
                          Ingresos Propios
                        </InputLabel>
                        <Select disabled={!canWrite || !capture.qa.enabled || capture.busy || capture.uncertain} onEdit={() => capture.setDirty(true)}
                          labelId="ingresos-l"
                          name="ingresosPropios"
                          value={
                            formData.demograficosBeneficiaria.ingresosPropios
                          }
                          label="Ingresos Propios"
                          onChange={(e) =>
                            handleChange("demograficosBeneficiaria", e)
                          }
                        >
                          <MenuItem value="Si">Si</MenuItem>
                          <MenuItem value="No">No</MenuItem>
                          <MenuItem value="N/A">No aplica (N/A)</MenuItem>
                        </Select>
                        {errors.demograficosBeneficiaria?.ingresosPropios && (
                          <FormHelperText>
                            {errors.demograficosBeneficiaria.ingresosPropios}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControl
                        fullWidth
                        margin="dense"
                        error={!!errors.demograficosBeneficiaria?.cabezaFamilia}
                      >
                        <InputLabel id="cabeza-l">Cabeza de Familia</InputLabel>
                        <Select disabled={!canWrite || !capture.qa.enabled || capture.busy || capture.uncertain} onEdit={() => capture.setDirty(true)}
                          labelId="cabeza-l"
                          name="cabezaFamilia"
                          value={
                            formData.demograficosBeneficiaria.cabezaFamilia
                          }
                          label="Cabeza de Familia"
                          onChange={(e) =>
                            handleChange("demograficosBeneficiaria", e)
                          }
                        >
                          <MenuItem value="Si">Si</MenuItem>
                          <MenuItem value="No">No</MenuItem>
                          <MenuItem value="N/A">No aplica (N/A)</MenuItem>
                        </Select>
                        {errors.demograficosBeneficiaria?.cabezaFamilia && (
                          <FormHelperText>
                            {errors.demograficosBeneficiaria.cabezaFamilia}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 4 }}>
                    <Chip
                      label="Datos de la atención"
                      sx={{ textTransform: "uppercase" }}
                    />
                  </Divider>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                      <FormControl
                        fullWidth
                        margin="dense"
                        error={!!errors.atencion?.pruebaEmbarazo}
                      >
                        <InputLabel id="prueba-emb-l">
                          Prueba de Embarazo
                        </InputLabel>
                        <Select disabled={!canWrite || !capture.qa.enabled || capture.busy || capture.uncertain} onEdit={() => capture.setDirty(true)}
                          labelId="prueba-emb-l"
                          name="pruebaEmbarazo"
                          value={formData.atencion.pruebaEmbarazo}
                          label="Prueba de Embarazo"
                          onChange={(e) => handleChange("atencion", e)}
                        >
                          <MenuItem value="Positivo">Positivo</MenuItem>
                          <MenuItem value="Negativo">Negativo</MenuItem>
                          <MenuItem value="No Aplica">No Aplica</MenuItem>
                        </Select>
                        {errors.atencion?.pruebaEmbarazo && (
                          <FormHelperText>
                            {errors.atencion.pruebaEmbarazo}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Código Método Anticonceptivo"
                        name="codigoAtencion"
                        value={formData.atencion.codigoAtencion}
                        onChange={(e) => handleChange("atencion", e)}
                        margin="dense"
                        error={!!errors.atencion?.codigoAtencion}
                        helperText={
                          errors.atencion?.codigoAtencion ||
                          `(Método Anticonceptivo) ${metodoAnticonceptivoRange.min}-${metodoAnticonceptivoRange.max}`
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        disabled
                        label="Método Anticonceptivo Colocado"
                        name="metodoAnticonceptivo"
                        value={formData.atencion.metodoAnticonceptivo}
                        margin="dense"
                        error={!!errors.atencion?.metodoAnticonceptivo}
                        helperText={errors.atencion?.metodoAnticonceptivo || ""}
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 4 }}>
                    <Chip
                      label="Manejo de I.T.S."
                      sx={{ textTransform: "uppercase" }}
                    />
                  </Divider>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                      <FormControl
                        fullWidth
                        margin="dense"
                        error={!!errors.manejoITS?.pruebaVIH}
                      >
                        <InputLabel id="vih-l">Prueba Rápida VIH</InputLabel>
                        <Select disabled={!canWrite || !capture.qa.enabled || capture.busy || capture.uncertain} onEdit={() => capture.setDirty(true)}
                          labelId="vih-l"
                          name="pruebaVIH"
                          value={formData.manejoITS.pruebaVIH}
                          label="Prueba Rápida VIH"
                          onChange={(e) => handleChange("manejoITS", e)}
                        >
                          <MenuItem value="Positivo">Positivo</MenuItem>
                          <MenuItem value="Negativo">Negativo</MenuItem>
                          <MenuItem value="No Aplica">No Aplica</MenuItem>
                        </Select>
                        {errors.manejoITS?.pruebaVIH && (
                          <FormHelperText>
                            {errors.manejoITS.pruebaVIH}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl
                        fullWidth
                        margin="dense"
                        error={!!errors.manejoITS?.pruebaSifilis}
                      >
                        <InputLabel id="sif-l">
                          Prueba Rápida Sífilis
                        </InputLabel>
                        <Select disabled={!canWrite || !capture.qa.enabled || capture.busy || capture.uncertain} onEdit={() => capture.setDirty(true)}
                          labelId="sif-l"
                          name="pruebaSifilis"
                          value={formData.manejoITS.pruebaSifilis}
                          label="Prueba Rápida Sífilis"
                          onChange={(e) => handleChange("manejoITS", e)}
                        >
                          <MenuItem value="Positivo">Positivo</MenuItem>
                          <MenuItem value="Negativo">Negativo</MenuItem>
                          <MenuItem value="No Aplica">No Aplica</MenuItem>
                        </Select>
                        {errors.manejoITS?.pruebaSifilis && (
                          <FormHelperText>
                            {errors.manejoITS.pruebaSifilis}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl
                        fullWidth
                        margin="dense"
                        error={!!errors.manejoITS?.pruebaHepatitis}
                      >
                        <InputLabel id="hep-l">Prueba de Hepatitis</InputLabel>
                        <Select disabled={!canWrite || !capture.qa.enabled || capture.busy || capture.uncertain} onEdit={() => capture.setDirty(true)}
                          labelId="hep-l"
                          name="pruebaHepatitis"
                          value={formData.manejoITS.pruebaHepatitis}
                          label="Prueba de Hepatitis"
                          onChange={(e) => handleChange("manejoITS", e)}
                        >
                          <MenuItem value="Positivo">Positivo</MenuItem>
                          <MenuItem value="Negativo">Negativo</MenuItem>
                          <MenuItem value="No Aplica">No Aplica</MenuItem>
                        </Select>
                        {errors.manejoITS?.pruebaHepatitis && (
                          <FormHelperText>
                            {errors.manejoITS.pruebaHepatitis}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Código Diagnóstico I.T.S."
                        name="codigoDiagnosticoITS"
                        value={formData.manejoITS.codigoDiagnosticoITS}
                        onChange={(e) => handleChange("manejoITS", e)}
                        margin="dense"
                        error={!!errors.manejoITS?.codigoDiagnosticoITS}
                        helperText={
                          errors.manejoITS?.codigoDiagnosticoITS ||
                          `(Diagnóstico I.T.S.) ${diagnosticoItsRange.min}-${diagnosticoItsRange.max}`
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        disabled
                        label="Diagnóstico Sindromático de I.T.S."
                        name="diagnosticoITS"
                        value={formData.manejoITS.diagnosticoITS}
                        onChange={(e) => handleChange("manejoITS", e)}
                        margin="dense"
                        error={!!errors.manejoITS?.diagnosticoITS}
                        helperText={errors.manejoITS?.diagnosticoITS || ""}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Autocomplete multiple freeSolo options={[]}
                        value={formData.manejoITS.tratamientoITS}
                        onChange={(event, values) => {
                          capture.setDirty(true);
                          handleAutocompleteChange("manejoITS", "tratamientoITS", values);
                        }}
                        renderInput={(params) => <TextField {...params} label="Tratamiento indicado ITS" />}
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 4 }}>
                    <Chip
                      label="Adicionales"
                      sx={{ textTransform: "uppercase" }}
                    />
                  </Divider>
                  <Grid container justifyContent="center" spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <AnticonDoctorSelect value={formData.adicionales.medicoResponsable}
                        disabled={!canWrite || capture.busy || capture.uncertain}
                        onChange={value => {
                          capture.setDirty(true);
                          handleAutocompleteChange("adicionales", "medicoResponsable", value);
                        }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Comentarios"
                        name="comentarios"
                        value={formData.adicionales.comentarios}
                        onChange={(e) => handleChange("adicionales", e)}
                        margin="dense"
                      />
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 4 }} />
                  <Grid container justifyContent="center">
                    <Grid item xs={12} sm={5}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          gap: 2,
                        }}
                      >
                        <Button
                          variant="contained"
                          color="error"
                          size="large"
                          fullWidth
                          onClick={handleOpen}
                        >
                          {" "}
                          Cancelar{" "}
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          fullWidth
                          disabled={!capture.contextReady || !canWrite || capture.busy || capture.conflict}
                          sx={{
                            backgroundColor: "teal",
                            "&:hover": { backgroundColor: "darkcyan" },
                          }}
                        >
                          {" "}
                          Guardar atención{" "}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                  </fieldset>
                </Box>
              </CardContent>
            </Card>
          </Container>
          {/*////////////////  MODAL CANCELAR  /////////////////*/}
          <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
            slotProps={{ backdrop: { style: { opacity: 0.2 } } }}
          >
            <Box sx={style}>
              <Typography variant="h6" className="text-center" sx={{ mb: 1.5 }}>
                ¿Está seguro de cancelar esta operación?
              </Typography>
              <Divider sx={{ mt: 2 }} />
              <Grid
                container
                sx={{ mt: 1 }}
                spacing={1}
                justifyContent="flex-end"
              >
                <Grid item>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleClose}
                  >
                    No
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    sx={{ bgcolor: "teal", color: "white" }}
                    onClick={(event) => handleLinkCancelar(event, "cancelar")}
                  >
                    Si
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Modal>
        </>
      ) : (
        <Restricted />
      )}
    </Box>
  );
}

export default AntiConFormV2;
